/**
 * UTM TRACKER — atribuição de canal/campanha por pedido (last-click por SUBSTITUIÇÃO)
 *
 * Grava a origem de mídia no cart (`_utm_source`, `_utm_medium`, `_utm_campaign`,
 * `_utm_content`, `_utm_term`, `_utm_ts`). Os atributos viajam cart → checkout → order,
 * então dá pra atribuir receita por campanha/anúncio via Admin API.
 *
 * ─── MODELO (decidido pelo dono, 26/Jul/2026) ───────────────────────────────
 * LAST-CLICK POR SUBSTITUIÇÃO: a UTM PERSISTE até chegar OUTRA UTM.
 *  - Veio de mídia paga, depois navegou/pesquisou orgânico e comprou → crédito FICA na mídia paga.
 *  - Recebeu e-mail do CRM (com utm) e comprou → crédito PASSA pro CRM.
 *  - Navegação interna, F5 e tráfego direto NÃO apagam nada.
 *
 * ⚠️ É DIFERENTE do `_lp_source` DE PROPÓSITO. Os dois eixos são COMPLEMENTARES:
 *      _utm_*      = QUEM TROUXE o cliente (não expira navegando)
 *      _lp_source  = O QUE CONVERTEU    (expira ao sair do funil — last-click PURO)
 *
 * ─── POR QUE NÃO HÁ CONFLITO (garantia do dono) ─────────────────────────────
 * 1. Este script escreve EXCLUSIVAMENTE chaves com prefixo `_utm_`. Nunca menciona
 *    `_lp_source` nem `_ab*` — não tem como apagá-los.
 * 2. O `/cart/update.js` do Shopify faz MERGE de atributos: as chaves não citadas no
 *    payload ficam intactas (validado contra a loja real em 26/Jul/2026).
 * 3. O `lp-attribution-guard.js` (que apaga) só limpa o que reconhece como dele:
 *    slug em `__lpTrackedSlugs` ou prefixo `pdp:`, mais a lista fixa `AB_KEYS`.
 *    Nada com prefixo `_utm_` bate nessas regras → o guard NUNCA apaga UTM.
 * 4. Este script NÃO tem guard/expiração: nada aqui apaga `_lp_source` nem `_ab*`.
 *
 * Como ativar: 1 linha no theme.liquid, em TODA página (sem guard de handle):
 *   <script src="{{ 'utm-tracker.js' | asset_url }}" defer></script>
 *
 * Guard rails: try/catch em tudo (jamais quebra a página); ZERO request quando não há
 * UTM na URL e o cart já está coerente com o cookie; header X-LP-Tracker (mesmo padrão
 * do lp-source-tracker.js, pra o interceptor do ecobag-gift.js reconhecer).
 */
(function () {
  'use strict';

  // Idempotência: não rodar 2x na mesma página
  if (window.__utmTrackerActive) return;
  window.__utmTrackerActive = true;

  var COOKIE_KEY = 'gd_utm';
  // 30d (mudado de 90d em 26/Jul/2026, decisão do dono): janela conservadora, alinhada à do
  // `_lp_source` (que já era 30d) — os dois eixos passam a expirar juntos, o que evita um
  // pedido com UTM antiga e LP recente. Encurtar torna a atribuição mais honesta: quem voltou
  // depois de um mês não credita mais o anúncio antigo.
  // ⚠️ No Safari/iOS o ITP corta cookie criado por JS em ~7 dias, então lá a janela real é 7d
  // (mesma limitação que o _lp_source já tem hoje — não é regressão). Impacto é ATENUADO:
  // o valor vive no CART (server-side) e sobrevive ao ITP enquanto o carrinho existir; o
  // cookie só serve pra RE-APLICAR se o carrinho for esvaziado. Efeito prático: cliente iOS
  // que clica no anúncio e volta semanas depois com carrinho vazio sai SEM atribuição —
  // ausente, nunca errada.
  var COOKIE_DAYS = 30;
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Campos capturados. `campaign` e `content` são TEXTO/NOME (decisão do dono) — não IDs.
  var FIELDS = ['source', 'medium', 'campaign', 'content', 'term'];
  var MAX_LEN = 200;

  // ── Classificação de canal (26/Jul/2026) ────────────────────────────────
  // REGRA: a UTM SEMPRE manda. Só quando NÃO há UTM o `document.referrer` decide.
  // Sem isso, uma visita de busca orgânica cairia no balde cego "sem UTM" — e um clique
  // pago (que também tem referrer de rede social) seria classificado como orgânico.
  //
  // ⚠️ O referrer só existe no PRIMEIRO pageview da sessão: depois de uma navegação interna
  // ele vira o próprio site. Por isso capturamos na primeira visita e guardamos no cookie,
  // igual à UTM — ler no momento da compra não funcionaria.
  //
  // Gravamos o canal JÁ CLASSIFICADO (não só o referrer cru) pra a regra ficar congelada no
  // momento da venda: ajustar a lista de buscadores depois não reescreve o histórico.
  var RE_BUSCA  = /google\.|bing\.|yahoo\.|duckduckgo\.|ecosia\.|search\.brave|yandex\.|baidu\./i;
  var RE_IA     = /chat\.openai|chatgpt\.com|perplexity\.ai|claude\.ai|gemini\.google|copilot\.microsoft/i;
  var RE_SOCIAL = /instagram\.|facebook\.|fb\.com|tiktok\.|youtube\.|twitter\.|x\.com|linkedin\.|pinterest\.|reddit\./i;
  var RE_PROPRIO = /guday\.com\.br|gummy-creatine\.myshopify/i;
  // Mediums que indicam MÍDIA PAGA. Lista ampla de propósito: um medium pago não
  // reconhecido cairia em "orgânico" e infl aria o orgânico enquanto sub-reporta a mídia —
  // erro que só apareceria meses depois, na hora de decidir verba. Cobre o que Meta, Google
  // e ferramentas de campanha costumam emitir.
  var RE_PAGO   = /cpc|ppc|paid|ads|display|banner|retarget|remarket|sponsor|native|discovery|pmax|performance.?max|shopping|affiliate|influencer/i;

  // Devolve { channel, detail }: channel = balde principal (Meta/Google/CRM/Orgânico/…),
  // detail = sub-classificação (busca/ia/social/…) — o dono quer UM balde "Orgânico" com
  // uma coluna indicando a origem dele.
  function classificar(utm, referrer) {
    var s = (utm.source || '').toLowerCase();
    var m = (utm.medium || '').toLowerCase();
    if (s) {
      var pago = RE_PAGO.test(m);
      if (/facebook|instagram|meta|^ig$|^fb$/.test(s)) return pago ? { channel: 'meta', detail: 'pago' } : { channel: 'organico', detail: 'social' };
      if (/google|youtube|gads|adwords/.test(s))       return pago ? { channel: 'google', detail: 'pago' } : { channel: 'organico', detail: 'busca' };
      if (/klaviyo|email|whatsapp|wpp|sms|crm/.test(s)) return { channel: 'crm', detail: m || 'crm' };
      return pago ? { channel: 'outros', detail: 'pago' } : { channel: 'outros', detail: m || '' };
    }
    var r = (referrer || '').trim();
    if (!r || RE_PROPRIO.test(r)) return { channel: 'direto', detail: '' };   // vazio ≠ prova de direto (app/privacidade)
    if (RE_BUSCA.test(r))  return { channel: 'organico', detail: 'busca' };
    if (RE_IA.test(r))     return { channel: 'organico', detail: 'ia' };
    if (RE_SOCIAL.test(r)) return { channel: 'organico', detail: 'social' };
    return { channel: 'referral', detail: hostDe(r) };
  }

  function hostDe(url) {
    try { return sanitize(new URL(url).hostname.replace(/^www\./, '')); }
    catch (e) { return ''; }
  }

  function safe(fn) {
    return function () {
      try { return fn.apply(null, arguments); }
      catch (e) { /* silencioso por design */ }
    };
  }

  // UTM vem de URL pública = entrada não confiável, e vai parar num relatório.
  // `campaign`/`content` são NOMES legíveis (decisão do dono), então o alfabeto permite
  // acentos/ç — sem isso "Campanha Julho Açaí" viraria "Campanha-Julho-Aa". Espaço vira
  // '-' pra não quebrar em query string. Tudo que sobra (<>"'&, etc) é removido.
  // Lixo que anúncio mal configurado manda como se fosse valor: `utm_source=undefined`
  // chega como a STRING "undefined" e passaria pelo filtro (é palavra válida), poluindo o
  // relatório e — pior — impedindo o referrer de classificar o pedido. Visto em produção
  // em 26/Jul/2026. Tratamos como ausente.
  var LIXO = /^(undefined|null|none|nan|false|0|-|na|n\/a|\{\{[^}]*\}\}|%7b%7b.*)$/i;

  function sanitize(v) {
    if (typeof v !== 'string') return '';
    var bruto = v.trim();
    // testa o LIXO ANTES de limpar: `{{campaign.name}}` (placeholder que o gerenciador de
    // anúncios não substituiu) viraria "campaign.name" e passaria como nome legítimo.
    if (LIXO.test(bruto) || /^\{\{|\}\}$|^%7b/i.test(bruto)) return '';
    var out = bruto
      .slice(0, MAX_LEN)
      .replace(/\s+/g, '-')
      .replace(/[^A-Za-z0-9À-ÖØ-öø-ÿ_.\-|]/g, '');
    return LIXO.test(out) ? '' : out;
  }

  function setCookie(name, value, days) {
    try {
      var expires = new Date(Date.now() + days * 86400000).toUTCString();
      document.cookie = name + '=' + encodeURIComponent(value) +
        '; expires=' + expires + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function getCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[2]) : null;
    } catch (e) { return null; }
  }

  function getCart() {
    return fetch(ROOT + 'cart.js', {
      headers: { 'Accept': 'application/json', 'X-LP-Tracker': '1' }
    }).then(function (r) { return r.json(); });
  }

  // Escreve SÓ chaves _utm_* (ver garantia nº 1 no cabeçalho).
  //
  // `replaceAll = true` (clique novo): a UTM nova substitui o CONJUNTO INTEIRO — campo que
  // não veio na URL é LIMPO (''), não herdado. Sem isso, o merge do Shopify preservaria o
  // valor antigo e o pedido sairia com campos de DUAS campanhas misturadas: um clique de
  // CRM (source=klaviyo, sem content) por cima de um clique do Face deixaria
  // `_utm_content = Criativo-A` do Facebook colado no pedido do CRM, e o relatório
  // creditaria ao criativo errado. Bug pego no QA de runtime (26/Jul/2026).
  //
  // `replaceAll = false` (restauração do cookie): só preenche o que falta, sem limpar nada.
  function writeAttributes(data, replaceAll) {
    var attrs = {};
    FIELDS.forEach(function (f) {
      if (data[f]) attrs['_utm_' + f] = data[f];
      else if (replaceAll) attrs['_utm_' + f] = '';   // '' = remove o atributo no Shopify
    });
    if (data.ts) attrs['_utm_ts'] = data.ts;
    // canal classificado + referrer de origem (só o host, não a URL inteira)
    if (data.channel) attrs['_utm_channel'] = data.channel;
    else if (replaceAll) attrs['_utm_channel'] = '';
    if (data.detail) attrs['_utm_detail'] = data.detail;
    else if (replaceAll) attrs['_utm_detail'] = '';
    if (data.referrer) attrs['_utm_referrer'] = data.referrer;
    else if (replaceAll) attrs['_utm_referrer'] = '';
    if (!Object.keys(attrs).length) return null;
    return fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-LP-Tracker': '1'
      },
      body: JSON.stringify({ attributes: attrs })
    });
  }

  // ── Captura ────────────────────────────────────────────────────────────

  // Lê ?utm_* da URL. Retorna null se NENHUM campo veio preenchido — `?utm_source=`
  // (vazio) conta como ausente e NÃO sobrescreve a UTM anterior.
  function readFromUrl() {
    var out = {}, found = false;
    try {
      var qs = new URLSearchParams(window.location.search);
      FIELDS.forEach(function (f) {
        var v = sanitize(qs.get('utm_' + f) || '');
        if (v) { out[f] = v; found = true; }
      });
    } catch (e) { return null; }
    if (!found) return null;
    out.ts = new Date().toISOString();
    return out;
  }

  // Origem SEM utm, deduzida do referrer. Só vale na PRIMEIRA pageview (depois o referrer
  // vira o próprio site). Retorna null quando não há sinal nenhum — aí não gravamos nada e
  // o pedido sai sem canal, em vez de inventar "direto" pra quem só navegou internamente.
  function readFromReferrer() {
    var r = '';
    try { r = document.referrer || ''; } catch (e) {}
    if (RE_PROPRIO.test(r)) return null;         // navegação interna: não é origem
    var c = classificar({}, r);
    if (c.channel === 'direto' && !r) {
      // sem referrer: pode ser marca (digitou), app ou privacidade. Registramos como
      // 'direto' MAS só se ainda não houver origem melhor gravada (tratado no run()).
      return { channel: 'direto', detail: '', referrer: '', ts: new Date().toISOString() };
    }
    return { channel: c.channel, detail: c.detail, referrer: hostDe(r), ts: new Date().toISOString() };
  }

  // O cookie é editável pelo usuário: revalida tipo E conteúdo antes de usar, senão um
  // cookie adulterado (ex: {"source":{"a":1}}) gravaria um objeto/número como valor do
  // atributo e sujaria o relatório. Passa tudo pelo mesmo sanitize() da URL.
  function readFromCookie() {
    try {
      var raw = getCookie(COOKIE_KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || typeof o !== 'object' || Array.isArray(o)) return null;
      var out = {}, found = false;
      FIELDS.forEach(function (f) {
        if (typeof o[f] !== 'string') return;      // só string; number/objeto/array caem fora
        var v = sanitize(o[f]);
        if (v) { out[f] = v; found = true; }
      });
      // canal/detail/referrer também contam como origem válida (caso sem UTM, via referrer)
      ['channel', 'detail', 'referrer'].forEach(function (k) {
        if (typeof o[k] !== 'string') return;
        var v = sanitize(o[k]);
        if (v) { out[k] = v; found = true; }
      });
      if (!found) return null;
      if (typeof o.ts === 'string') out.ts = sanitize(o.ts);
      return out;
    } catch (e) { return null; }
  }

  // ── Core ───────────────────────────────────────────────────────────────

  var run = safe(function () {
    var fromUrl = readFromUrl();

    // CLIQUE NOVO COM UTM: substitui (last-click). A UTM SEMPRE ganha do referrer — um
    // anúncio pago também chega com referrer de rede social, e sem essa precedência ele
    // seria classificado como orgânico. Grava cookie na hora (não depende da rede).
    if (fromUrl) {
      var refUrl = '';
      try { refUrl = document.referrer || ''; } catch (e) {}
      var cls = classificar(fromUrl, refUrl);
      fromUrl.channel = cls.channel;
      fromUrl.detail = cls.detail;
      fromUrl.referrer = RE_PROPRIO.test(refUrl) ? '' : hostDe(refUrl);
      setCookie(COOKIE_KEY, JSON.stringify(fromUrl), COOKIE_DAYS);
      getCart().then(function (cart) {
        try {
          var a = (cart && cart.attributes) || {};
          var same = FIELDS.every(function (f) {
            return (a['_utm_' + f] || '') === (fromUrl[f] || '');
          }) && (a._utm_channel || '') === (fromUrl.channel || '');
          if (same) return;                        // cart já idêntico → sem request
          return writeAttributes(fromUrl, true);   // clique novo → substitui o conjunto inteiro
        } catch (e) {}
      }).catch(function () {});
      return;
    }

    // SEM UTM NA URL. Duas situações:
    //  (a) já existe origem gravada (cookie) → só restaura no cart se ele tiver perdido.
    //  (b) NÃO existe origem → é uma PRIMEIRA visita sem UTM: o referrer vira a origem
    //      (busca orgânica, IA, social não-paga, referral). Só aqui, porque o referrer
    //      some depois da primeira navegação interna.
    var saved = readFromCookie();

    if (!saved) {
      var fromRef = readFromReferrer();
      if (!fromRef) return;                        // nem cookie nem referrer → nada a fazer
      setCookie(COOKIE_KEY, JSON.stringify(fromRef), COOKIE_DAYS);
      getCart().then(function (cart) {
        try {
          var a = (cart && cart.attributes) || {};
          // Não sobrescreve uma origem que já esteja no cart (ex: veio de outro device/sessão).
          if (a._utm_source || a._utm_channel) return;
          return writeAttributes(fromRef, false);
        } catch (e) {}
      }).catch(function () {});
      return;
    }

    // (a) restauração: nunca sobrescreve, só preenche o que falta no cart.
    getCart().then(function (cart) {
      try {
        var a = (cart && cart.attributes) || {};
        var missing = FIELDS.some(function (f) {
          return saved[f] && !a['_utm_' + f];
        }) || (saved.channel && !a._utm_channel);
        if (!missing) return;                 // cart já coerente → sem request
        return writeAttributes(saved, false); // restauração → só preenche o que falta
      } catch (e) {}
    }).catch(function () {});
  });

  // Folga de 500ms pra deixar o interceptor do ecobag-gift.js instalar primeiro —
  // espelha o init do lp-source-tracker.js.
  setTimeout(run, 500);
})();
